import { Component } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { NuclearObservable } from '../../nuclear-observable'
import { BaseComponent } from '../../components/base.component'
import { User, Account, BillingAccount, Country, State, Customer } from '../../types'
import { geoGetters } from '../../stores/geo-store'
import { customerGetters } from '../../stores/customer-store'
import { UserActions } from '../../actions/user-actions'
import { AccountActions } from '../../actions/account-actions'
import { GeoActions } from '../../actions/geo-actions'
import { CustomerActions } from '../../actions/customer-actions'
import { KeywordActions } from '../../actions/keyword-actions'
import { AnalyticsActions } from '../../actions/analytics-actions'
import { isAgencySignup } from '../../utils'
import { MediaQueryService } from '../../services/media-query-service'
import { Title } from '@angular/platform-browser'

@Component({
  selector: 'nc-payment-page-v1',
  template: `
    <split-view>
      <div left>
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-12">
              <h2 class="nc-section-header mt-3">
                <div class="nc-section-icon far fa-credit-card"></div>
                <span class="ml-3">Subscription Summary</span>
                <ng-template [ngIf]="isAgencySignup()">
                  <br><span class="small"><i class="fas fa-arrow--left" [class.fa-arrow-left]="!isMobile" [class.fa-arrow-up]="isMobile"></i> or add another client</span>
                </ng-template>
              </h2>
              <div class="mt-4">
                <account-activation-summary *ngIf="showActivationSummary()"></account-activation-summary>
              </div>
              <div *ngIf="showPlanPageLink()">
                <p class="top-buffer">You need to <a [routerLink]="['../', 'plan']">select a plan</a> before you can activate</p>
                <button class="float-left orange-button top-buffer" [routerLink]="['../', 'plan']">Select a plan</button>
              </div>

              <subscription-review
                [summaryData]="summaryData"
                [withTitle]="false"
              ></subscription-review>
            </div>
          </div>
        </div>
      </div>

      <div right>
        <ng-template [ngIf]="showPlanPageLink()">
          <div class="container-fluid big-top-buffer">
            <div class="row">
              <div class="col-12">
                <p>Awaiting plan selection</p>
              </div>
            </div>
          </div>
        </ng-template>
        <ng-template [ngIf]="showActivationSummary()">
          <div class="container-fluid big-top-buffer">
            <div class="row">
              <div class="col-12">
                <p>Enter your payment information</p>
              </div>
            </div>
            <div class="row bottom-buffer">
              <div class="col-12">
                <div class="loader" *ngIf="countries.length == 0">loading...</div>
                <payment-info-form [currentUser]="currentUser" [account]="account" [billingAccount]="billingAccount" [countries]="countries" [states]="states" *ngIf="statesLoaded" [newCustomer]="true" (onSubmitSuccess)="navigateToNextPage()"></payment-info-form>
              </div>
            </div>
          </div>
        </ng-template>
      </div>
    </split-view>
  `
})
export class NcPaymentPageV1 extends BaseComponent {
  account: Account;
  currentUser: User;
  billingAccount: BillingAccount;
  countries: Country[] = [];
  states: State[] = [];
  statesLoaded: boolean = false;
  summaryData = {}
  private customerId: number;
  private customer: Customer;

  customers: any[]

  constructor(private userActions: UserActions,
              private geoActions: GeoActions,
              private route: ActivatedRoute,
              private keywordActions: KeywordActions,
              private accountActions: AccountActions,
              private customerActions: CustomerActions,
              private analyticsActions: AnalyticsActions,
              private mediaQueryService: MediaQueryService,
              private titleService: Title
  ) {
    super()
  }

  ngOnInit(): void {
    this.titleService.setTitle('Sign Up - Activation')
    this.observeUser(u => this.currentUser = u)
    this.observeAccount(a => this.account = a)
    this.observeCustomers(a => this.customers = a)
    const billingObs = NuclearObservable.from<BillingAccount>(customerGetters.billingAccount, { toJS: true }).subscribe(ba => this.billingAccount = ba)
    const countriesObs = NuclearObservable.from<Country[]>(geoGetters.countries, { toJS: true }).subscribe(countries => this.countries = countries)

    this.userActions.loadUser()
    this.accountActions.loadAccount()
    this.geoActions.loadCountries()

    // Load the recurly account
    this.accountActions.loadBillingAccount().then(() => {
      const selectedCountry = this.billingAccount && this.billingAccount.address ? this.billingAccount.address.country || 'US' : 'US'
      this.geoActions.loadStatesForCountry(selectedCountry).then(() => {
        this.states = NuclearObservable.currentValue<State[]>(geoGetters.states(selectedCountry))
        this.statesLoaded = true
      })
    })

    const routeObs = this.route.params.subscribe(params => {
      this.clearRouteObservers()
      this.customerId = +params['customerId']
      if (this.customerId) {
        this.customerActions.loadCustomer(this.customerId)
        this.keywordActions.loadKeywords(this.customerId)
        this.observeCustomer(this.customerId, c => this.customer = c)
      }
    })
    this.observers.push(routeObs, billingObs, countriesObs)

    const summaryJson = localStorage.getItem('summary')
    if (summaryJson) {
      this.summaryData = JSON.parse(summaryJson)
    }
  }

  showActivationSummary(): boolean|any {
    return this.isAgencySignup() || this.account?.hasPlan
  }

  showPlanPageLink(): boolean {
    return !this.isAgencySignup() && !this.account?.hasPlan
  }

  navigateToNextPage(): void {
    localStorage.removeItem('summary')
    const kickstart = `/dashboard/${this.customerId}/kickstart`
    const dashboardShortcut = 'shortcuts/signup_success'
    let subscriptionSummary = {}
    if (this.customers) {
      subscriptionSummary = this.customers[0]?.subscription?.subscriptionSummary ?? {}
    }

    this.analyticsActions.activated(
      this.account.id,
      this.account.totalPendingValueWithDiscount / 100,
      this.account.agency ? 'agency' : 'business',
      subscriptionSummary,
      true).then(() => {
      if (this.isAgencySignup()) {
        if (this.account.customersCount > 1)
          window.location.pathname = dashboardShortcut
        else
          window.location.pathname = 'shortcuts/kickstart'
      } else {
        // Verblio Complete skips kickstart
        if ((this.customer?.subscription?.planAddOns || []).map(p => p.code).includes('option-c')) {
          window.location.pathname = dashboardShortcut
        } else {
          window.location.pathname = kickstart
        }
      }
    }).catch(() => window.location.pathname = kickstart)
  }

  isAgencySignup(): boolean {
    return isAgencySignup(this.account)
  }

  get isMobile(): boolean {
    return this.mediaQueryService.isUnder('lg')
  }

}
